import { useReducer, useState, useEffect } from 'react';
import { isSSR } from '@front10/utils';
import { useTravelGuideTracking } from '../../../../_integrations/everymundo/tracking/travelGuide';

const states = {
  INITIAL: 'INITIAL',
  HIDDEN: 'HIDDEN',
  SKELETON: 'SKELETON',
  COMPLETE: 'COMPLETE',
  ERROR: 'ERROR',
  LOADING: 'LOADING',
  NO_RESULTS: 'NO_RESULTS'
};
const transitions = {
  [states.INITIAL]: {
    feed: states.COMPLETE,
    hide: states.HIDDEN,
    load: states.SKELETON,
    show_no_results: states.NO_RESULTS
  },
  [states.SKELETON]: {
    success: states.COMPLETE,
    failure: states.ERROR,
    show_no_results: states.NO_RESULTS,
    hide: states.HIDDEN
  },
  [states.COMPLETE]: {
    load: states.LOADING
  },
  [states.LOADING]: {
    success: states.COMPLETE,
    show_no_results: states.NO_RESULTS,
    failure: states.ERROR
  },
  [states.NO_RESULTS]: {
    load: states.LOADING
  },
  [states.ERROR]: {
    load: states.LOADING
  }
};

function reducer(prev, action) {
  const nextState = transitions[prev.value][action.transition];
  if (nextState) {
    return {
      ...prev,
      value: nextState,
      history: prev.value,
      ...action.payload
    };
  }
  return prev;
}

function getResultsLength(results) {
  if (!results.title || !results.articles || !results.sections) {
    return 0;
  }
  return results.sections.length;
}

function useQuery({ getData, active = true, initialData }, deps = []) {
  const [firstLoad, setFirstLoad] = useState(initialData);

  useEffect(() => {
    if (!active) {
      return;
    }
    if (firstLoad && initialData) {
      setFirstLoad(false);
      return;
    }
    setFirstLoad(false);
    getData();
    // eslint-disable-next-line
  }, [active, ...deps]);
}

export function useTravels({
  prePopulation,
  integrationSettings,
  showNoResultsPlaceholder = true,
  initialData,
  getTravelsGuide,
  meta,
  pageContext
}) {
  let initialState = transitions[states.INITIAL].load;
  if (initialData) {
    const resultsLength = getResultsLength(initialData);
    if (resultsLength === 0 && !showNoResultsPlaceholder) {
      initialState = transitions[states.INITIAL].hide;
    } else if (showNoResultsPlaceholder && resultsLength === 0) {
      initialState = transitions[states.INITIAL].show_no_results;
    } else {
      initialState = transitions[states.INITIAL].feed;
    }
  }

  const [{ value: state, data: travelsGuide, history }, send] = useReducer(reducer, {
    value: initialState,
    data: initialData,
    history: undefined
  });

  const getData = () => {
    if (transitions[state].load) {
      send({ transition: 'load' });
    }
    return getTravelsGuide(prePopulation, integrationSettings, meta, pageContext)
      .then((_data) => {
        const itemCount = getResultsLength(_data);
        if (transitions[state].hide && itemCount === 0) {
          send({ transition: 'hide' });
        } else if (showNoResultsPlaceholder && itemCount === 0) {
          send({ transition: 'show_no_results', payload: { data: _data } });
        } else {
          send({ transition: 'success', payload: { data: _data } });
        }
      })
      .catch((err) => {
        send({ transition: 'failure' });
        throw err;
      });
  };

  useQuery({ getData, initialData }, [integrationSettings, prePopulation]);

  function match(stateValue) {
    if (isSSR() && stateValue === 'SKELETON') {
      return true;
    }
    return stateValue === state || (state === states.LOADING && history === stateValue);
  }

  const track = useTravelGuideTracking();

  function dispatch(action) {
    track(state, action);
  }

  return {
    travelsGuide,
    match,
    retry: () => getData(),
    dispatch
  };
}
